import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import uuid from 'react-uuid';

import FormHeader from './FormHeader';
import FormOverview from './FormOverview';
import ProductionSheet from './ProductionSheet';
import Divider from '@mui/material/Divider';

import styles from './OrderForm.module.css';
import Loading from '../../../../Layout/Loading';
import ToastAlert from '../../../../UI/Alert/ToastAlert';
import { orderActions } from '../../../../store/order-slice';
import { SetProducts, updateOrder } from '../../../../store/order-actions';
import { syncXeroInvoiceToOrder } from '../../../../store/xero-actions'
import { useNavigate } from 'react-router-dom';
import { OrderConstant } from '../../../../Constants';

import Button from '@mui/material/Button';
import { SyncProblem, CloudSync } from '@mui/icons-material';

const dateFormat = 'DD/MM/YYYY hh:mm a'

// Added: centralise per-product total calculation so row and order total use the same formula.
const calculateProductTotal = (product) => {
    const qty = Number(product.qty || 0);
    const unitCost = Number(product.unitCost || 0);
    const deliveryFee = Number(product.deliveryFee || 0);
    const packaging = Number(product.packaging || 0);

    return qty * unitCost + deliveryFee + packaging;
}

// Added: always derive order total from the current products array.
const calculateOrderTotal = (products) => {
    return products.reduce((sum, product) => {
        return sum + Number(product.totalAmt || 0);
    }, 0);
}

// Added: local product template so add-product can use the same local state as row edits.
const createEmptyProduct = () => {
    return {
        id: uuid(),
        name: '',
        qty: 0,
        unitCost: 0,
        deliveryFee: 0,
        packaging: 0,
        specification: '',
        printing: '',
        packagingInstruction: '',
        images: [],
        cld_images: [],
        productSupplier: { name: '', shipDate: null, shipMode: '', cost: 0, trackingRef: '' },
        printMaterialSupplier: { name: '', shipDate: null, shipMode: '', cost: 0, trackingRef: '' },
        printServiceSupplier: { name: '', shipDate: null, shipMode: '', cost: 0, trackingRef: '' },
        totalAmt: 0,
        exportable: true,
        uploaded: false
    };
}

const OrderForm = ({ order, xeroitem }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [validForm, setValidForm] = useState(true);
    const [edited, setEdited] = useState(false);
    const image_product_edited = useSelector(state => state.order.image_product_edited);

    const isUpdating = useSelector(state => state.order.req_order_update_loading);
    const updateResponse = useSelector(state => state.order.req_order_update_response);
    const updateError = useSelector(state => state.order.req_order_update_error);

    // Old: add/delete product error came only from Redux product actions.
    // const productErr = useSelector(state => state.order.add_product_error);
    const reduxProductErr = useSelector(state => state.order.add_product_error);
    const imageErr = useSelector(state => state.order.add_image_error);
    const deleteErr = useSelector(state => state.order.req_order_delete_error);
    const deleteErrMsg = deleteErr ? 'Error deleting order, please refresh and try again.' : null;
    const hasSalesRepOptionErr = useSelector(state => state.user.req_error);
    const hasOptionErr = useSelector(state => state.option.req_options_error);
    // Added: local add/delete product flow needs its own error state because it no longer dispatches Redux product mutations.
    const [localProductErr, setLocalProductErr] = useState(null);
    // Added: merge legacy Redux product error with the new local add/delete product error.
    const productErr = localProductErr || reduxProductErr;
    const hasProductError = !!productErr || !!imageErr || !!deleteErr || !!hasSalesRepOptionErr || !!hasOptionErr;

    const deleteSuccessRes = useSelector(state => state.order.req_order_delete_response);

    //Alert state
    const [openSnackbar, setOpenSnackBar] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const [snackType, setSnackType] = useState('success');

    const [formHeaderInputData, setFormHeaderInputData] = useState({
        orderNo: order.orderNo,
        status: order.status,
        createdAt: dayjs(order.createdAt).format(dateFormat),
        salesrep: order.salesrep,
        updatedAt: dayjs(order.updatedAt).format(dateFormat),
        updatedBy: order.updatedBy,
        paymentStatus: order.paymentStatus,
        binded: order.xeroInvoiceId !== '',
        xeroInvoiceId: order.xeroInvoiceId
    });

    const [formOverviewInputData, setFormOverviewInputData] = useState({
        salesrepid: order.salesrepid,
        salesrep: order.salesrep,
        salesrepContact: order.salesrepContact,
        poNo: order.poNo,
        quoteNo: order.quoteNo,
        invoiceNo: order.invoiceNo,
        sampleDelivery: order.sampleDelivery,
        massOrderDelivery: order.massOrderDelivery,
        paymentTerm: order.paymentTerm,
        paymentMode: order.paymentMode,
        paymentDueDate: order.paymentDueDate
    });
    
    const [formDeliveryInputData, setFormDeliveryInputData] = useState({
        date: order.delivery.date,
        status: order.delivery.status,
        note: order.delivery.note,
        logs: order.delivery.logs
    })

    const [client, setClient] = useState({
        companyName: order.client.companyName,
        billingAddr: order.client.billingAddr,
        clientName: order.client.clientName,
        deliveryAddr: order.client.deliveryAddr,
        contact: order.client.contact,
        email: order.client.email,
        remark: order.client.remark
    })

    const [products, setProducts] = useState(order.products)

    // Old: stored order total separately in component state, which could go stale after add/delete/Xero product updates.
    // const [totalAmtBeforeGST, setTotalAmtBeforeGST] = useState(order.totalAmtBeforeGST)

    // Added: always derive order total from current products so it stays in sync.
    const totalAmtBeforeGST = calculateOrderTotal(products)

    useEffect(() => {
        setProducts(order.products);
    }, [order.products])

    useEffect(() => {
        if (xeroitem) {
            setFormHeaderInputData({
                ...formHeaderInputData,
                xeroInvoiceId: xeroitem?.quoteInv?.id ?? '',
                paymentStatus: xeroitem.paymentStatus,
                binded: (xeroitem?.quoteInv?.id ?? '') !== ''
            })

            setClient({
                ...client,
                companyName: xeroitem.companyName,
                billingAddr: xeroitem.billingAddr ?? '',
                deliveryAddr: xeroitem.deliveryAddr ?? '',
                clientName: xeroitem.contactInfo?.name ?? '',
                contact: xeroitem.contactPhone ?? '',
                email: xeroitem.contactInfo?.email ?? ''
            })

            if (xeroitem.quoteInv) {
                if (xeroitem.quoteInv.type === 'Quote') {
                    setFormOverviewInputData({
                        ...formOverviewInputData,
                        quoteNo: xeroitem.quoteInv.name,
                        invoiceNo: '',
                        paymentDue: ''
                    })
                } else {
                    // const dueDate = xeroitem.quoteInv.duedate;
                    // const paymentDue = dueDate ? dayjs(dueDate).format('DD-MMM-YYYY') : '';
                    
                    setFormOverviewInputData({
                        ...formOverviewInputData,
                        quoteNo: '',
                        invoiceNo: xeroitem.quoteInv.name,
                        paymentDueDate: xeroitem.quoteInv.duedate
                    })
                }

                // Old: manually tracked a separate order total during Xero populate.
                // This could drift from products if products changed again later.
                // var totalcost = 0;

                // Keep: Xero still replaces products through the existing Redux flow.
                dispatch(SetProducts(xeroitem.quoteInv.LineItems))

                // Old: manual Xero total accumulation is no longer needed because totalAmtBeforeGST is derived from products.
                // xeroitem.quoteInv.LineItems.forEach(li => {
                //     totalcost += (li.LineAmount ?? 0)
                // })

                // Old: separate total state update removed.
                // setTotalAmtBeforeGST(totalcost);
            }
        }
        //eslint-disable-next-line
    }, [xeroitem])

    //Form validation
    useEffect(() => {
        if (formOverviewInputData.salesrepid === '' || !!productErr || !!imageErr) {
            setValidForm(false);
            return
        }

        //assuming everything checks out, final check on edited.
        setValidForm(edited || image_product_edited);
    }, [formOverviewInputData.salesrepid, edited, productErr, imageErr, image_product_edited])

    useEffect(() => {
        if (updateResponse) {
            setSnackType('success');
            setSnackMessage('Order update successful!')
            setOpenSnackBar(true)
            setEdited(false);
        }

        if (updateError) {
            setSnackType('error');
            setSnackMessage(updateError);
            setOpenSnackBar(true);
            setEdited(true);
        }
    }, [updateResponse, updateError])

    useEffect(() => {
        if (deleteSuccessRes) {
            setSnackType('success');
            setSnackMessage('Order deleted')
            setOpenSnackBar(true)
            //dispatch(orderActions.Reset_Update_Order_State());
            //navigate('/order');
        }
    }, [deleteSuccessRes])

    const handleFormHeaderInputChange = (event) => {
        setEdited(true);
        const inputFieldValue = event.target.value;
        const inputFieldName = event.target.name;

        const newInputValue = {
            ...formHeaderInputData,
            [inputFieldName]: inputFieldValue
        };

        setFormHeaderInputData(newInputValue)
    };

    const handleFormOverviewInputChange = (event) => {
        setEdited(true);
        const inputFieldValue = event.target.value;
        const inputFieldName = event.target.name;

        let newInputValue = { ...formOverviewInputData, [inputFieldName]: inputFieldValue };

        if (inputFieldName === 'salesrepACField') {
            if (inputFieldValue) {
                newInputValue.salesrepid = inputFieldValue._id;
                newInputValue.salesrep = `${inputFieldValue.firstname} ${inputFieldValue.lastname}`.trim();
                newInputValue.salesrepContact = inputFieldValue.contact;
            } else {
                newInputValue.salesrepid = '';
                newInputValue.salesrep = '';
            }
        }

        setFormOverviewInputData(newInputValue);
    }

    const handleClientInputChange = (event) => {
        setEdited(true);
        const inputFieldValue = event.target.value;
        const inputFieldName = event.target.name;

        let newInputValue = { ...client, [inputFieldName]: inputFieldValue };

        setClient(newInputValue);
    }

    const handleProductInputChange = (event, index) => {
        
        setEdited(true);
        var inputFieldValue = event.target.value;
        const inputFieldName = event.target.name;
        
        const productCostUpdated = inputFieldName === 'qty' || inputFieldName === 'unitCost';
        //|| inputFieldName === 'deliveryFee' || inputFieldName === 'packaging';

        if(productCostUpdated){
            inputFieldValue = +inputFieldValue;
        }else if(inputFieldName === 'exportable'){
            inputFieldValue = event.target.checked
        }

        // Old: manually accumulating order total inside the row edit handler.
        // let totalcost = 0;

        const newProducts = products.map((prod, i) => {
            if (i === index) {
                const clonedProd = { ...prod, [inputFieldName]: productCostUpdated ? +inputFieldValue : inputFieldValue }

                if (productCostUpdated) {
                    // Old: partial formula used only qty * unitCost, which can be wrong if packaging/deliveryFee exists.
                    // //clonedProd.totalAmt = clonedProd.qty * clonedProd.unitCost + clonedProd.deliveryFee + clonedProd.packaging;
                    // clonedProd.totalAmt = clonedProd.qty * clonedProd.unitCost;

                    // Added: use the shared formula expected by backend.
                    clonedProd.totalAmt = calculateProductTotal(clonedProd);
                }

                // Old: manual order total accumulation no longer needed.
                // totalcost += clonedProd.totalAmt
                return clonedProd
            }

            // Old: manual order total accumulation no longer needed.
            // totalcost += prod.totalAmt;

            return prod;
        })

        setProducts(newProducts);
        // Old: removed because totalAmtBeforeGST is now derived from products.
        // setTotalAmtBeforeGST(totalcost)
    }

    const handleAddProduct = () => {
        setEdited(true);

        // Old: add product used Redux action in ProductionSheet, which updated order.products and could overwrite local row edits.
        // dispatch(addProduct());

        // Added: keep add-product in the same local products state as row edits to avoid reverting unsaved changes.
        if (products.length >= OrderConstant.MaxProductAllowed) {
            setLocalProductErr(`Max product limit hit. You can only add up to ${OrderConstant.MaxProductAllowed} products.`);
            return;
        }

        setLocalProductErr(null);
        setProducts(prevProducts => [...prevProducts, createEmptyProduct()]);
    }

    const handleDeleteProduct = (productId) => {
        setEdited(true);

        // Old: delete product used Redux action in ProductItem, which updated order.products and could overwrite local row edits.
        // dispatch(deleteProduct(product.id));

        // Added: keep delete-product in the same local products state as row edits to avoid reverting unsaved changes.
        setLocalProductErr(null);
        setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
    }

    const handleDeliveryInputChange = (event) => {
        setEdited(true);

        const inputFieldValue = event.target.value;
        const inputFieldName = event.target.name;

        let newInputValue = { ...formDeliveryInputData, [inputFieldName]: inputFieldValue };

        setFormDeliveryInputData(newInputValue);
    }

    const onSubmitHandler = () => {
        const id = order._id;

        const submittingOrder = {
            status: formHeaderInputData.status,
            xeroInvoiceId: formHeaderInputData.xeroInvoiceId,
            paymentStatus: formHeaderInputData.paymentStatus,
            ...formOverviewInputData,
            client,
            products,
            delivery: formDeliveryInputData,
            totalAmtBeforeGST,
            revision: order.revision
        }

        dispatch(updateOrder(id, submittingOrder));
    }

    const onalertClose = () => {
        setOpenSnackBar(false)
        dispatch(orderActions.Reset_Update_Order_State());

        if (deleteSuccessRes) {
            navigate('/order')
        }
    }

    return (
        <>
            {isUpdating && <Loading loadingText="" style={{ width: '100%' }} />}

            <div className={styles.container}>
                {
                    hasProductError &&
                    <div className={styles['error-banner']}>
                        {productErr || imageErr || deleteErrMsg || hasSalesRepOptionErr || hasOptionErr}
                    </div>
                }
                {
                    order.xeroPendingSync &&
                    <div className={styles['error-banner']} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <SyncProblem fontSize='small' />
                        Order is out of sync with Xero Invoice.
                        
                        <Button 
                            size='small' 
                            variant="contained" 
                            endIcon={<CloudSync/>}
                            sx={{fontSize:'0.6rem'}}
                            onClick={() => dispatch(syncXeroInvoiceToOrder(order))}>
                            Sync
                        </Button>
                    </div>
                }
                <FormHeader
                    formData={formHeaderInputData}
                    handleInputChange={handleFormHeaderInputChange}
                    submitHandler={onSubmitHandler}
                    validform={validForm}
                    order={order} />
                <FormOverview
                    formHeader={formHeaderInputData}
                    formData={formOverviewInputData}
                    handleInputChange={handleFormOverviewInputChange} />
                <Divider />
                <ProductionSheet
                    formHeader={formHeaderInputData}
                    clientFormData={client}
                    productFormData={products}
                    deliveryFormData={formDeliveryInputData}
                    handleClientInputChange={handleClientInputChange}
                    handleProductInputChange={handleProductInputChange}
                    handleDeliveryInputChange={handleDeliveryInputChange}
                    onAddProduct={handleAddProduct}
                    deleteProduct={handleDeleteProduct}
                    overallcost={totalAmtBeforeGST} />

                {
                    hasProductError &&
                    <div className={styles['error-banner']}>
                        {productErr || imageErr}
                    </div>
                }
            </div>

            <ToastAlert
                open={openSnackbar}
                onClose={onalertClose}
                message={snackMessage}
                type={snackType}
            />
        </>
    )
}

export default OrderForm
